import { ActionIcon, Anchor, Button, FileButton, Group, Stack, Text } from '@mantine/core';
import { IconDownload, IconUpload, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  OsLotReviewProposedTelepac,
  OsLotReviewProvenance,
  OsLotReviewTelepacArtifact,
} from '../../lib/types/os-lot-reviews';

import classes from './os-lot-reviews.module.css';

function artifactLabel(artifact: OsLotReviewTelepacArtifact): string {
  if (artifact.telepac_id && artifact.campaign_year) {
    return `${artifact.telepac_id} (${artifact.campaign_year})`;
  }
  return artifact.telepac_id || 'TelePAC.xml';
}

function TelepacDownloadLink({
  artifact,
}: {
  artifact: OsLotReviewTelepacArtifact;
}) {
  const { t } = useTranslation();
  if (!artifact.file_url) {
    return (
      <Text c="gray.7" size="sm">
        {artifactLabel(artifact)}
      </Text>
    );
  }
  return (
    <Anchor
      download
      href={artifact.file_url}
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      <Group gap={6} wrap="nowrap">
        <IconDownload aria-hidden size={14} stroke={1.8} />
        {t('osLotReviews.telepac.download', { name: artifactLabel(artifact) })}
      </Group>
    </Anchor>
  );
}

export function OsLotReviewTelepacSource({
  provenance,
}: {
  provenance: OsLotReviewProvenance | null | undefined;
}) {
  const { t } = useTranslation();

  if (!provenance || provenance.method === 'NONE' || provenance.method === 'FAILED') {
    return (
      <div className={classes.telepacSource}>
        <p className={classes.telepacLabel}>{t('osLotReviews.provenance')}</p>
        <Text size="sm">{t('osLotReviews.telepac.none')}</Text>
      </div>
    );
  }

  if (provenance.method === 'SHAPE_DIRECT') {
    return (
      <div className={classes.telepacSource}>
        <p className={classes.telepacLabel}>{t('osLotReviews.provenance')}</p>
        <Text size="sm">{t('osLotReviews.telepac.shape')}</Text>
      </div>
    );
  }

  const artifact = provenance.telepac_artifact;
  const year =
    provenance.reference_campaign_year ?? artifact?.campaign_year ?? null;
  const telepacId = provenance.applied_telepac_id ?? artifact?.telepac_id ?? null;

  if (provenance.method === 'RPG_IDENTIFIED') {
    return (
      <div className={classes.telepacSource}>
        <p className={classes.telepacLabel}>{t('osLotReviews.provenance')}</p>
        <Text size="sm">
          {t('osLotReviews.telepac.identified_from', {
            id: telepacId ?? '—',
            year: year ?? '—',
          })}
        </Text>
        {artifact ? <TelepacDownloadLink artifact={artifact} /> : null}
      </div>
    );
  }

  return (
    <div className={classes.telepacSource}>
      <p className={classes.telepacLabel}>{t('osLotReviews.telepac.used_file')}</p>
      {artifact ? (
        <TelepacDownloadLink artifact={artifact} />
      ) : (
        <Text size="sm">{telepacId ?? t('osLotReviews.telepac.none')}</Text>
      )}
    </div>
  );
}

export function OsLotReviewProposedTelepacList({
  files,
}: {
  files: OsLotReviewProposedTelepac[];
}) {
  if (files.length === 0) {
    return null;
  }
  return (
    <Stack gap={4}>
      {files.map((file) => (
        <Anchor
          download
          href={file.url}
          key={`${file.filename}-${file.url}`}
          rel="noreferrer"
          size="sm"
          target="_blank"
        >
          <Group gap={6} wrap="nowrap">
            <IconDownload aria-hidden size={14} stroke={1.8} />
            {file.filename}
          </Group>
        </Anchor>
      ))}
    </Stack>
  );
}

export function OsLotReviewTelepacImport({
  files,
  onChange,
}: {
  files: File[];
  onChange: (files: File[]) => void;
}) {
  const { t } = useTranslation();

  const addFiles = (incoming: File | File[] | null) => {
    if (!incoming) {
      return;
    }
    const next = Array.isArray(incoming) ? incoming : [incoming];
    const byName = new Map(files.map((file) => [file.name, file]));
    next.forEach((file) => {
      byName.set(file.name, file);
    });
    onChange([...byName.values()]);
  };

  return (
    <Stack gap="xs">
      <FileButton
        accept=".xml,text/xml,application/xml"
        multiple
        onChange={addFiles}
      >
        {(props) => (
          <Button
            {...props}
            fullWidth
            leftSection={<IconUpload size={16} />}
            variant="light"
          >
            {t('osLotReviews.telepac.import')}
          </Button>
        )}
      </FileButton>
      {files.length > 0 ? (
        <Text c="gray.7" size="sm">
          {t('osLotReviews.telepac.selected', { count: files.length })}
        </Text>
      ) : null}
      {files.map((file) => (
        <Group gap="xs" justify="space-between" key={file.name} wrap="nowrap">
          <Text size="sm" truncate="end">
            {file.name}
          </Text>
          <ActionIcon
            aria-label={t('osLotReviews.telepac.remove', { name: file.name })}
            onClick={() =>
              onChange(files.filter((item) => item.name !== file.name))
            }
            variant="subtle"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      ))}
    </Stack>
  );
}
